import { WikiInspectionType } from '../../api/jurisdiction';
import { apiSlice } from './apiSlice';
import { FormFieldGroup } from '../../organisms/ThresholdReports/PreviewTool/builder/staticFieldOptions';

/**
 * The page size every "give me all the forms" caller must use.
 *
 * RTK Query keys its cache on the serialized query args, so callers asking for
 * the same data with different limits each trigger their own request. The list
 * response carries every page image as base64 (megabytes), so a mismatch here
 * is not a rounding error — it doubles the download.
 */
export const FORM_LIST_LIMIT = 1000;

const api = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getInspectionForms: builder.query<any, any>({
      query: params => ({
        url: '/inspection_form_builder/',
        params,
      }),
      providesTags: ['thresholdForms'],
    }),
    /**
     * One form, WITH its page images.
     *
     * The list endpoint omits `file_base64` — it is ~99% of that payload and is
     * only needed once a specific form has been matched. Anything that draws a
     * form (the fill canvas, the PDF, the builder preview) reads the images
     * from here instead. Deliberately NOT tagged 'thresholdForms': a markup
     * PATCH invalidates that tag, and re-downloading megabytes of unchanged
     * page images on every autosave is the problem this split exists to fix.
     */
    getInspectionForm: builder.query<any, number>({
      query: id => ({ url: `/inspection_form_builder/${id}/` }),
      // Scoped to this one form. `thresholdForms` is invalidated by every
      // markup save, and re-downloading unchanged page images on each of those
      // is exactly what the list/detail split exists to avoid — but replacing
      // the DOCUMENT really does change the images, so that path invalidates
      // this tag explicitly.
      providesTags: (result, error, id) => [{ type: 'thresholdFormFile', id }],
    }),

    /**
     * Update a form in place, optionally replacing its document.
     *
     * Editing used to POST whenever a file was attached, which created a
     * duplicate form and left the original untouched. This always PATCHes the
     * form in hand; the server keeps `form_markup`, so placements survive a
     * document swap.
     */
    updateInspectionForm: builder.mutation({
      query: arg => ({
        url: `/inspection_form_builder/${arg.id}/`,
        method: 'PATCH',
        body: arg.data,
      }),
      invalidatesTags: (result, error, arg) => [
        'thresholdForms',
        { type: 'thresholdFormFile', id: arg.id },
      ],
    }),
    saveInspectionFormData: builder.mutation({
      query: arg => {
        return {
          url: `inspection_form_builder/`,
          method: 'POST',
          body: arg,
        };
      },
      invalidatesTags: ['thresholdForms'],
    }),
    deleteThresholdForm: builder.mutation({
      query: arg => {
        return {
          url: `/inspection_form_builder/${arg.id}/`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['thresholdForms'],
    }),
    updateFormMarkup: builder.mutation({
      query: arg => {
        return {
          url: `/inspection_form_builder/${arg.id}/`,
          method: 'PATCH',
          body: arg.data,
        };
      },
      invalidatesTags: ['thresholdForms'],
    }),
    /**
     * What a static field can be mapped to: the Regular Forms field list,
     * grouped by the record it is read off. Generated by the server from
     * form_setup/form_fields.py plus the Global Variables configured in
     * Settings. Kept for the whole session rather than refetched by every field
     * card that mounts; the Global Variables page invalidates the tag whenever
     * it adds, edits or deletes one.
     */
    getFormFieldMappings: builder.query<{ groups: FormFieldGroup[] }, void>({
      query: () => ({ url: '/inspection_form_builder/field-mappings/' }),
      providesTags: ['formFieldMappings'],
      keepUnusedDataFor: 360000000,
    }),
  }),
});

export const {
  useSaveInspectionFormDataMutation,
  useGetInspectionFormsQuery,
  useGetInspectionFormQuery,
  useUpdateInspectionFormMutation,
  useDeleteThresholdFormMutation,
  useUpdateFormMarkupMutation,
  useGetFormFieldMappingsQuery,
} = api;
